// src/services/achievementService.ts
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';
import { getStoredLanguage } from './languageService';
import { isAuthenticated } from './authService'; 

export interface AchievementObjective {
  id: number;
  objective_hash: number;
  name: string | null;
  description: string | null;
  progress_description: string | null;
  completion_value: number;
  progress?: number;
  is_completed?: boolean;
}

export interface Step {
  id: number;
  name?: string;
  description?: string;
  completed?: boolean;
  progress?: {
    current: number;
    total: number;
  };
}

export interface Achievement {
  id: number;
  record_hash: number;
  name: string;
  description: string;
  icon_url: string | null;
  is_secret: boolean;
  is_completed?: boolean;
  completion_date?: string | null;
  points: number;
  category_type_id?: number | null;
  category_name?: string | null;
  progress: {
    current: number;
    total: number;
    percentage: number;
    exact_percentage?: number;
  };
  objectives: AchievementObjective[];
  steps?: Step[];
}

export interface Category {
  id: number;
  name: string;
  display_name: string;
  icon_url: string | null;
  ordering: number;
  subcategories: Category[];
  achievements: Achievement[];
}

export interface Group {
  id: number;
  display_name: string;
  description: string;
  icon_url: string | null;
  categories: Category[];
}

/**
 * Fetches all achievements grouped in their hierarchy
 */
export const getGroupedAchievements = async (): Promise<Group[]> => {
  try {
    // Add language parameter to URL
    const language = getStoredLanguage();
    let url = `${API_CONFIG.BASE_URL}/achievements/grouped?lang=${language}`;
    
    // Add user=true parameter if user is authenticated
    const userIsAuthenticated = isAuthenticated();
    if (userIsAuthenticated) {
      url += '&user=true';
    }
    
    const response = await axios.get(url);
    
    if (response.data.status === 'success') {
      return response.data.data;
    }
    throw new Error('Failed to fetch achievements: ' + response.data.message);
  } catch (error) {
    console.error('Error fetching grouped achievements:', error);
    throw error;
  }
};


/**
 * Fetches details for a specific achievement
 */
export const getAchievementDetails = async (id: number): Promise<Achievement | null> => {
  try {
    const language = getStoredLanguage();
    // Base URL with achievement ID
    let url = `${API_CONFIG.BASE_URL}/achievements?action=detail&id=${id}&lang=${language}`;
    
    // Add user=true parameter if user is authenticated
    const userIsAuthenticated = isAuthenticated();
    if (userIsAuthenticated) {
      url += '&user=true';
    }
   
    const response = await axios.get(url);
    
    if (response.data.status === 'success') {
      // Mark response if it came from an authenticated user
      const data = response.data.data;
      data._authenticated = userIsAuthenticated;
      return data;
    }
    throw new Error('Failed to load achievement details: ' + response.data.message);
  } catch (error) {
    console.error('Error loading achievement details:', error);
    return null;
  }
};

/**
 * Flatten the hierarchical achievement data for easier display
 * Useful when you need a simple list of all achievements
 */
export const flattenAchievements = (groups: Group[]): Achievement[] => {
  const flattenedAchievements: Achievement[] = [];
  
  const processCategory = (category: Category) => {
    // Add achievements from this category
    flattenedAchievements.push(...category.achievements);
    
    // Process subcategories recursively
    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach(subcategory => processCategory(subcategory));
    }
  };
  
  groups.forEach(group => {
    group.categories.forEach(category => processCategory(category));
  });
  
  return flattenedAchievements;
};